import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CursorTrailStyle } from '@/types/content';

interface Particle {
  id: number;
  x: number;
  y: number;
  createdAt: number;
}

interface CursorTrailProps {
  style: CursorTrailStyle;
  enabled: boolean;
}

const styleConfig: Record<CursorTrailStyle, { emoji: string; colors: string[] }> = {
  stars: { emoji: '⭐', colors: ['#FFD700', '#FFA500', '#FFFF00'] },
  sparkles: { emoji: '✨', colors: ['#FFD700', '#87CEEB', '#FF69B4'] },
  rainbow: { emoji: '●', colors: ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#8B00FF'] },
  hearts: { emoji: '❤️', colors: ['#FF69B4', '#FF1493', '#DC143C'] },
  fire: { emoji: '🔥', colors: ['#FF4500', '#FF6347', '#FFD700'] },
};

const CursorTrail = ({ style, enabled }: CursorTrailProps) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [colorIndex, setColorIndex] = useState(0);

  const config = styleConfig[style];

  const addParticle = useCallback((x: number, y: number) => {
    const newParticle: Particle = {
      id: Date.now() + Math.random(),
      x,
      y,
      createdAt: Date.now(),
    };
    setParticles(prev => [...prev.slice(-20), newParticle]);
    setColorIndex(prev => (prev + 1) % config.colors.length);
  }, [config.colors.length]);

  useEffect(() => {
    if (!enabled) {
      setParticles([]);
      return;
    }

    let lastX = 0;
    let lastY = 0;
    let throttleTimer: number | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Only add particle if moved enough distance and throttle
      if (distance > 15 && !throttleTimer) {
        lastX = e.clientX;
        lastY = e.clientY;
        addParticle(e.clientX, e.clientY);
        
        throttleTimer = window.setTimeout(() => {
          throttleTimer = null;
        }, 50);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Cleanup old particles
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setParticles(prev => prev.filter(p => now - p.createdAt < 1000));
    }, 100);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(cleanupInterval);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [enabled, addParticle]);

  if (!enabled) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      <AnimatePresence>
        {particles.map((particle, index) => (
          <motion.div
            key={particle.id}
            initial={{ 
              opacity: 1, 
              scale: 1,
              x: particle.x - 10,
              y: particle.y - 10,
            }}
            animate={{ 
              opacity: 0, 
              scale: 0,
              y: particle.y - 50,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="absolute text-xl"
            style={{
              color: config.colors[index % config.colors.length],
            }}
          >
            {style === 'rainbow' ? (
              <span 
                className="w-3 h-3 rounded-full block"
                style={{ 
                  backgroundColor: config.colors[index % config.colors.length],
                  boxShadow: `0 0 6px ${config.colors[index % config.colors.length]}`,
                }}
              />
            ) : (
              config.emoji
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default CursorTrail;